import React, { createContext, useContext, useEffect, useState } from 'react';
import PingWorker from '../pingWorker.worker';
import {
  formatVariables,
  treatMediaToLocalStorage,
} from '../utils/formatValues';

import {
  getNewMessages,
  getNewHistoricMessages,
  createStatus,
  formatMessage,
  verifyMessageAlreadyExists,
  formatNewMessage,
} from '../utils/messengerUtils';

import LocalStorageUtils from '../utils/localStorageUtils';
import {
  importScriptRybena,
  closePlayer,
  stopTranslate,
} from '../utils/rybena';
import { ChatContext } from './ChatContext';
import { SettingsContext } from './SettingsContext';
import { getBotData } from '../services/getBotData';
import {
  setMessages,
  cleanMessages,
  messages,
  setNewArrayMessages,
} from './messagesMemory';

// eslint-disable-next-line import/no-named-as-default-member
import * as Extensions from '../constants/fileExtensions';
import messageTypes from '../constants/messageTypes';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL;
const API_URL = process.env.REACT_APP_API_URL;
const CHANNEL = 'webchat';
const FILE_SIZE_LIMIT = 15 * 1024 * 1024; // 15MB
const MAX_ATTEMPTS = 3;

const MessengerProvider = ({ children }) => {
  const { bot, customSettings, setIsNotFound } = useContext(SettingsContext);
  const { _id: botId } = bot;
  const [hasConnection, setHasConnection] = useState(false);
  const [hasToSubscribe, setHasToSubscribe] = useState(false);
  const [isHumanStarted, setIsHumanStarted] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const { variables, webchatType } = customSettings;
  const {
    setOnLocalStorage,
    removeOnLocalStorage,
    getOnLocalStorage,
    removeBotIdFromCloseActions,
  } = LocalStorageUtils();
  const [, setIsReconnecting] = useState(false);
  const {
    currentBotId,
    setCurrentBotId,
    clientRef,
    workerRef,
    socketId,
    setSocketId,
    sessionId,
    setHistoricMessages,
    setUniqueSessionIds,
    setSessionMessages,
    setImgSrc,
    setImgTitle,
    setFullScreen,
    fullScreen,
    setSessionId,
    isMute,
    widgetIsFocused,
    updateScrollToTop,
    playNotificationSound,
    lastSessionId,
    setLastSessionId,
    conversationIsStarted,
    pageIsOpen,
    setHandleResetTranslate,
    webchatIsStarted,
    timeoutId,
    setInterruptMessage,
    setTranslateMessages,
    setClientMessge,
    setTimeoutId,
    setResetChat,
    uniqueSessionIds,
    setHasHistoryToLoad,
    setHasIdentifierClient,
    workerVersionRef,
    setWorkerVersion,
    workerVersion,
  } = useContext(ChatContext);

  const resetWorker = () => {
    if (workerRef?.current) {
      workerRef.current.terminate();
      workerRef.current = null;
    }
    const worker = new PingWorker();
    workerRef.current = worker;
    setWorkerVersion((prev) => prev + 1);
  };

  const connectSocket = (isDisconnect = false) => {
    if (clientRef.current) {
      clientRef.current.onclose = null;
      clientRef.current.close();
      clientRef.current = null;
      setSocketId(null);
    }

    const client = new WebSocket(SOCKET_URL);
    clientRef.current = client;

    const worker = new PingWorker();
    workerRef.current = worker;

    client.addEventListener('open', () => {
      const sessionId = clientRef?.sessionId || getOnLocalStorage('session-id');
      if (isDisconnect && !sessionId) return;

      client.send(
        JSON.stringify({
          action: 'link',
          data: { sessionId, botId },
        })
      );

      setIsReconnecting(true);
    });

    client.addEventListener('error', (evt) => {
      console.log('error', evt);
    });

    client.onclose = () => {
      console.log('closed connection');
      connectSocket(true);
    };
  };

  const resetChatWithoutConnect = (botId, timeoutId) => {
    setCurrentBotId(botId);
    setSessionId('');
    removeOnLocalStorage([
      'session-id',
      'session-messages',
      'session-params',
      'humanStarted',
    ]);
    setSessionMessages([]);
    cleanMessages();
    setHistoricMessages([]);
    setUniqueSessionIds([]);
    setHasHistoryToLoad(true);
    setHasIdentifierClient(false);
    setHasConnection(false);
    clearTimeout(timeoutId);
    setTimeoutId(null);
    setIsNotFound(true);
    setIsHumanStarted(false);
    resetWorker();
  };

  const handleReset = (reconect = true) => {
    cleanMessages();
    setHistoricMessages([]);
    setUniqueSessionIds([]);
    setSessionMessages([]);
    setHasHistoryToLoad(true);
    setHasIdentifierClient(false);
    setHasConnection(false);
    stopTranslate();
    setResetChat(true);
    closePlayer('reset', setHandleResetTranslate);
    removeOnLocalStorage([
      'session-id',
      'session-messages',
      'session-params',
      'humanStarted',
    ]);
    setCurrentBotId(botId);
    setSessionId('');
    clearTimeout(timeoutId);
    setTimeoutId(null);
    setIsHumanStarted(false);
    resetWorker();
    reconect && connectSocket();
  };

  const handleStatusMessage = (message, status) => {
    const currentSessionId =
      sessionId ||
      getOnLocalStorage('session-id') ||
      lastSessionId ||
      message?.sessionId;

    if (!clientRef.current || !currentSessionId) return;

    fetch(`${API_URL}/status`, {
      method: 'POST',
      body: JSON.stringify({
        sessionId: currentSessionId,
        botId,
        status,
        messageId: message?.id,
      }),
    });
  };

  const handleExistingSession = (undeliveredBotMessages) => {
    const statusMessage = createStatus(
      webchatType,
      widgetIsFocused,
      pageIsOpen
    );
    let newMessages = [];

    if (webchatType === 'fullscreen') {
      newMessages = messages;
    } else {
      const messagesOnStorage = getOnLocalStorage('session-messages');
      newMessages = messagesOnStorage ? JSON.parse(messagesOnStorage) : [];
    }

    newMessages = [
      ...newMessages,
      ...(undeliveredBotMessages
        ?.map((undeliveredMessage) => {
          const messageAlreadyExists = undeliveredMessage?.id
            ? verifyMessageAlreadyExists(newMessages, undeliveredMessage?.id)
            : false;
          if (!messageAlreadyExists) {
            handleStatusMessage(undeliveredMessage, statusMessage);
            return formatMessage(undeliveredMessage, 'bot', statusMessage);
          }
          return undefined;
        })
        .filter((message) => message !== undefined) || []),
    ];

    setNewArrayMessages(newMessages);
    setSessionMessages(newMessages);
    setOnLocalStorage('session-messages', JSON.stringify(newMessages));
    updateScrollToTop();
  };

  const subscribe = async (data) => {
    const onlyGetUndeliveryMessages = data?.onlyGetUndeliveryMessages;
    if (!socketId) {
      return connectSocket();
    }

    const currentSessionId =
      webchatType === 'fullscreen'
        ? sessionId
        : getOnLocalStorage('session-id');

    if (currentSessionId) {
      setSessionId(currentSessionId);
    }

    if (onlyGetUndeliveryMessages && !currentSessionId) return;

    const botData = await getBotData({ botId });
    if (botData && botData.apiRybena) {
      importScriptRybena(botData);
    }
    if (botData && botData?.channel?.isActive) {
      if (botId) {
        const dataResponse = await fetch(`${API_URL}/subscribe`, {
          method: 'POST',
          body: JSON.stringify({
            socketId,
            sessionId: currentSessionId,
            botId,
            isPreview: false,
            channel: CHANNEL,
            parameters: JSON.stringify(formatVariables(variables)),
            onlyGetUndeliveryMessages,
          }),
        })
          .then((response) => {
            setAttempt(0);
            return response.json();
          })
          .catch((error) => {
            console.error(`Error to subscribe:`, error);
            setAttempt((prev) => prev + 1);
          });

        if (attempt >= MAX_ATTEMPTS - 1) {
          console.error('Error: All reconnection attempts failed.');
          setAttempt(0);
          return;
        }

        const newSessionId = dataResponse?.sessionId;
        const isNewSession = dataResponse?.isNewSession;
        const undeliveredBotMessages =
          dataResponse?.undeliveredBotMessages || [];

        if (!newSessionId && !onlyGetUndeliveryMessages) {
          return handleReset();
        }

        if (isNewSession) {
          removeBotIdFromCloseActions(botId);
          setIsHumanStarted(false);
          removeOnLocalStorage([
            'session-id',
            'session-messages',
            'session-params',
            'humanStarted',
          ]);
        } else {
          handleExistingSession(undeliveredBotMessages);
          const humanState =
            webchatType === 'fullscreen'
              ? isHumanStarted
              : getOnLocalStorage('humanStarted');

          if (humanState !== undefined && humanState !== null) {
            const isValid = humanState === true || humanState === 'true';

            setIsHumanStarted(isValid);
          }
          if (onlyGetUndeliveryMessages) return;
        }

        setSessionId(newSessionId);
        setOnLocalStorage('session-id', newSessionId);
      }
    } else {
      resetChatWithoutConnect();
    }
  };

  const onClientMessage = async ({ data: strData }) => {
    const { action, data = {} } = JSON.parse(strData);

    if (action === 'humanStarted') {
      setIsHumanStarted(data);
      if (data === false) {
        removeOnLocalStorage('humanStarted');
        resetWorker();
        setOnLocalStorage('humanStarted', data);
      }
    }
    if (action === 'link') {
      const { socketId } = data;
      setSocketId(socketId);
    } else if (action === 'end_conversation') {
      console.log(
        `(${sessionId}) - Receiving end_conversation - ConnectionId: ${socketId}`
      );
      if (!data?.isTransfer) {
        if (currentBotId) {
          setCurrentBotId(botId);
        }
        setLastSessionId(sessionId);
        setSessionId(null);
        removeOnLocalStorage([
          'session-id',
          'session-messages',
          'session-params',
        ]);
        setHasConnection(false);
        setSessionMessages([]);
        setIsHumanStarted(false);
        resetWorker();
      }
    } else if (action === 'message') {
      if (!sessionId && data?.sessionId) {
        setSessionId(data.sessionId);
      }
      const statusMessage = createStatus(
        webchatType,
        widgetIsFocused,
        pageIsOpen
      );

      setMessages(formatNewMessage(data, data?.from || 'bot', statusMessage));

      if (data.type !== 'TYPING') {
        handleStatusMessage(data, statusMessage);
        playNotificationSound('receiving', isMute);
      }

      setSessionMessages((prevMessages) => {
        const newMessages = getNewMessages(
          prevMessages,
          data,
          data?.from || 'bot'
        );
        if (data.type !== 'TYPING') {
          setOnLocalStorage('session-messages', JSON?.stringify(newMessages));
        }
        return newMessages;
      });

      updateScrollToTop();
    }

    if (action === 'historicMessage') {
      data.forEach((el) => {
        setHistoricMessages((prevMessages) => {
          return getNewHistoricMessages(
            prevMessages,
            el,
            el?.from || 'bot',
            ''
          );
        });
      });
    }

    if (action === 'identifierClient') {
      const { sessionId } = data;
      if (sessionId) {
        uniqueSessionIds.push(sessionId);
        setHasIdentifierClient(true);
      }
    }
  };

  const handleSubmitMessage = async ({
    message,
    type,
    ext,
    size,
    properties = {},
  }) => {
    let mediaTypeError;
    if (Extensions.malicious.includes(ext)) {
      message = messageTypes.MEDIA_ERROR_MESSAGE;
      mediaTypeError = messageTypes.MALICIOUS_FILE;
    }
    if (size > FILE_SIZE_LIMIT) {
      message = messageTypes.MEDIA_ERROR_MESSAGE;
      mediaTypeError = messageTypes.BIGGER_FILE;
    }

    const messageTreated = message.trim();
    const dataToMessage = {
      message: messageTreated,
      type,
      ...properties,
    };

    let hasToSubscribe = false;

    setClientMessge(true);
    setInterruptMessage(true);
    const translateMessages = messages.map((message) => {
      const messageType = message?.type?.toLowerCase();
      const messageToTranslate = message?.message;
      return {
        ...message,
        message: messageType === 'text' ? messageToTranslate : messageType,
        isTranslate: true,
      };
    });

    setNewArrayMessages(translateMessages);
    stopTranslate();

    setMessages(formatNewMessage(dataToMessage, 'user'));

    setSessionMessages((prevMessages) => {
      const newMessages = getNewMessages(
        prevMessages,
        treatMediaToLocalStorage(dataToMessage),
        'user'
      );
      setOnLocalStorage('session-messages', JSON?.stringify(newMessages));
      return newMessages;
    });

    playNotificationSound('sending', isMute);
    if (!sessionId) {
      console.log(
        `(${sessionId}) - Subscribing by submit message - ConnectionId: ${socketId}`
      );
      subscribe();
    } else {
      stopTranslate();
      const isMedia = type !== 'TEXT';
      const payload = {
        sessionId,
        botChannel: CHANNEL,
        botId: currentBotId || botId,
        message: messageTreated,
        isMedia,
        ext,
        isPreview: false,
        socketId,
        ...(mediaTypeError && { mediaTypeError }),
      };

      try {
        const responseData = await fetch(`${API_URL}/message`, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        const response = await responseData.json();
        if (!response) return console.log('Empty response', response);
        if (response.isNewSession) {
          removeOnLocalStorage([
            'session-id',
            'session-messages',
            'session-params',
          ]);
          setSessionId(response.sessionId);
          setOnLocalStorage('session-id', response.sessionId);
        }
        if (response.socketIsActive === false) {
          setSocketId(null);
          setHasConnection(false);
          hasToSubscribe = true;
        }
        if (response.hasUndeliveredMessages) {
          hasToSubscribe = true;
        }
      } catch (error) {
        console.error(error);
      }
    }

    setTranslateMessages(translateMessages);
    updateScrollToTop();
    if (hasToSubscribe) {
      setHasToSubscribe(true);
    }
  };

  const handleCarouselButtonClick = (clickInfo) => {
    const { clickedButton, clickedCard } = clickInfo;
    const { destination } = clickedButton;
    const { type, value } = destination;

    if (type === 'url') {
      window.open(value, '_blank');
    }

    if (type === 'phone') {
      window.open(`tel:${value}`, '_blank');
    }

    if (!sessionId) {
      subscribe();
    } else {
      fetch(`${API_URL}/action`, {
        method: 'POST',
        body: JSON.stringify({
          sessionId,
          botId: currentBotId || botId,
          action: { type: 'CAROUSEL', data: { clickedButton, clickedCard } },
        }),
      }).catch((error) => console.log('Error to execute action: ', error));
    }
  };

  const handleQuickAccessClick = ({ itemId, title }) => {
    console.log({ itemId, title });
    if (!sessionId) {
      subscribe();
    } else {
      fetch(`${API_URL}/quick-access`, {
        method: 'POST',
        body: JSON.stringify({
          sessionId,
          botId: currentBotId || botId,
          itemId,
          name: title,
          isPreview: false,
        }),
      }).catch((error) => console.log('Error to quick-access: ', error));
    }
  };

  const handleClickImage = (src, title) => {
    setImgSrc(src);
    setImgTitle(title);
    setFullScreen(!fullScreen);
  };

  const updateStatusMessages = (messages) => {
    return messages.map((message) => {
      if (message.status === 'delivered') {
        handleStatusMessage(message, 'read');
        return {
          ...message,
          status: 'read',
        };
      }
      return message;
    });
  };

  const handleLoadPreviousMessages = async () => {
    await fetch(`${API_URL}/historicMessage`, {
      method: 'POST',
      body: JSON.stringify({
        uniqueSessionIds,
        botId: currentBotId || botId,
        currentSession: sessionId,
        socketId,
      }),
    })
      .then((response) => {
        if (response.status !== 200) {
          setHasHistoryToLoad(false);
        }
      })
      .catch((error) =>
        console.log('Error retrieving historic messages: ', error)
      );
  };

  useEffect(() => {
    if (clientRef.current) {
      setIsReconnecting(false);
      setSessionId(sessionId);
      clientRef.current.onmessage = onClientMessage;
    }
  }, [
    socketId,
    clientRef?.current,
    isMute,
    currentBotId,
    sessionId,
    widgetIsFocused,
    conversationIsStarted,
  ]);

  useEffect(() => {
    if (hasToSubscribe) {
      subscribe({ onlyGetUndeliveryMessages: true });
      setHasToSubscribe(false);
    }
  }, [hasToSubscribe]);

  useEffect(() => {
    if (pageIsOpen && botId && sessionId) {
      subscribe({ onlyGetUndeliveryMessages: true });
    }
  }, [pageIsOpen]);

  useEffect(() => {
    resetWorker();
    console.log(
      `(${sessionId}) - Changing socketId - ConnectionId: ${socketId}`
    );
    if (socketId && !hasConnection) {
      setHasConnection(true);
      console.log(
        `(${sessionId}) - Subscribing by changing socketId - ConnectionId: ${socketId}`
      );
      subscribe();
    }
  }, [socketId]);

  useEffect(() => {
    if (clientRef) {
      clientRef.sessionId = sessionId;
    }
  }, [sessionId]);

  /* UseEffect responsável por enviar uma ação ao API Gateway a cada 9 minutos para garantir que a conexão do Socket persista. */
  useEffect(() => {
    if (clientRef?.current) {
      const sendKeepingConnection = () => {
        clientRef.current.send(
          JSON.stringify({
            action: 'message',
            data: {
              action: 'keepingConnection',
              sessionId: clientRef.sessionId || sessionId,
              botId,
            },
          })
        );
      };
      const intervalId = setInterval(sendKeepingConnection, 9 * 60 * 1000);

      return () => {
        clearInterval(intervalId);
      };
    }
  }, [clientRef?.current]);

  useEffect(() => {
    workerVersionRef.current = workerVersion;
    if (clientRef?.current && workerRef?.current && isHumanStarted === true) {
      workerRef.current.postMessage({ interval: 15000 });
      const handleMessage = () => {
        if (
          clientRef.current.readyState !== 1 ||
          workerVersion !== workerVersionRef.current ||
          !navigator.onLine
        ) {
          return;
        }

        clientRef.current.send(
          JSON.stringify({
            action: 'message',
            data: {
              action: 'validateConnection',
              sessionId: clientRef.sessionId || sessionId,
              botId,
            },
          })
        );
      };
      workerRef.current.onmessage = handleMessage;
      return () => {
        workerRef.current.onmessage = null;
      };
    }
  }, [workerVersion, isHumanStarted]);

  // UseEffect responsável por fazer o subscribe ao abrir o widget
  useEffect(() => {
    if (webchatIsStarted && !hasConnection) {
      console.log(
        `(${sessionId}) - Subscribing by webchat starts - ConnectionId: ${socketId}`
      );
      subscribe();
    }
  }, [webchatIsStarted]);

  return (
    <MessengerContext.Provider
      value={{
        updateScrollToTop,
        handleClickImage,
        handleQuickAccessClick,
        handleCarouselButtonClick,
        handleLoadPreviousMessages,
        handleSubmitMessage,
        handleReset,
        getNewMessages,
        getNewHistoricMessages,
        playNotificationSound,
        handleStatusMessage,
        subscribe,
        updateStatusMessages,
        connectSocket,
      }}
    >
      {children}
    </MessengerContext.Provider>
  );
};

export default MessengerProvider;
export const MessengerContext = createContext();
export const useMessengerContext = () => useContext(MessengerContext);
